import { styled } from '@linaria/react';
import {
  bodyFont,
  fonts,
  layout,
  primitives,
  semantic,
  shadow,
  size,
  space,
} from '../../theme/tokens';
import type { ModalSize } from './Modal.types';

export const Container = styled.div<{ $open: boolean; $aboveMobileNav?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $aboveMobileNav }) => ($aboveMobileNav ? 101 : layout.zIndexModal)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${layout.desktopBreakpoint}) {
    align-items: flex-end;
    padding-bottom: ${({ $aboveMobileNav }) =>
      $aboveMobileNav
        ? `calc(${layout.mobileNavHeight} + env(safe-area-inset-bottom, 0px))`
        : 'env(safe-area-inset-bottom, 0px)'};
  }
`;

export const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: ${semantic.bg.overlayModal};
  backdrop-filter: blur(${size.blur['blur-lg']});
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity 250ms ease;
`;

const mobileMaxHeight = ($aboveMobileNav?: boolean, $size?: ModalSize) => {
  if ($aboveMobileNav)
    return `calc(100dvh - var(--shell-mobile-nav-height, 0px) - env(safe-area-inset-bottom, 0px))`;
  if ($size === 'full') return `calc(100dvh - env(safe-area-inset-bottom, 0px))`;
  if ($size === 'lg')
    return `calc(100dvh - ${space['space-1000']} - env(safe-area-inset-bottom, 0px))`;
  return 'calc(85dvh - env(safe-area-inset-bottom, 0px))';
};

export const Panel = styled.div<{
  $open: boolean;
  $aboveMobileNav?: boolean;
  $size?: ModalSize;
}>`
  position: relative;
  outline: none;
  background: ${semantic.bg.canvas};
  border-radius: ${({ $size }) => ($size === 'full' ? '0' : size.radius['radius-xxl'])};
  box-shadow: ${shadow.lg};
  display: flex;
  flex-direction: column;
  height: ${({ $size }) => ($size === 'full' ? '100dvh' : 'auto')};
  max-height: ${({ $size }) => ($size === 'full' ? '100dvh' : '80vh')};
  width: ${({ $size }) =>
    $size === 'full' ? '100vw' : $size === 'lg' ? layout.modalWidthLg : layout.modalWidth};
  max-width: ${({ $size }) => ($size === 'full' ? '100vw' : `calc(100vw - ${space['space-600']})`)};
  min-width: ${({ $size }) => ($size === 'full' ? '0' : 'auto')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transform: ${({ $open }) => ($open ? 'translateY(0)' : `translateY(${space['space-400']})`)};
  transition: opacity 250ms ease, transform 250ms ease;

  @media (max-width: ${layout.desktopBreakpoint}) {
    width: 100vw;
    max-width: 100vw;
    max-height: ${({ $aboveMobileNav, $size }) => mobileMaxHeight($aboveMobileNav, $size)};
    border-radius: ${({ $size }) =>
      $size === 'full' ? '0' : `${size.radius['radius-xl']} ${size.radius['radius-xl']} 0 0`};
    transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(100%)')};
  }
`;

export const CloseRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${space['space-300']};
  flex-shrink: 0;

  @media (max-width: ${layout.desktopBreakpoint}) {
    border-bottom: ${size.stroke['stroke-default']} solid ${semantic.border.default};
  }
`;

export const TitleRow = styled.div`
  padding: 0 ${space['space-600']} ${space['space-800']};
  flex-shrink: 0;

  @media (max-width: ${layout.desktopBreakpoint}) {
    padding: ${space['space-800']} ${space['space-600']} ${space['space-600']};
  }
`;

export const Title = styled.h2`
  font-family: ${bodyFont};
  font-size: ${fonts.size.xl};
  font-weight: ${fonts.weight.normal};
  line-height: ${primitives.typography.lineHeight.tight};
  letter-spacing: ${primitives.typography.tracking.normal};
  color: ${semantic.text.primary};
  margin: 0;
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: ${space['space-200']};
  border: none;
  border-radius: ${size.radius['radius-full']};
  background: none;
  color: ${semantic.icon.primary};
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${semantic.bg.surfaceSubtle};
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const Body = styled.div<{ $noPadding?: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: ${({ $noPadding }) => ($noPadding ? '0' : `0 ${space['space-600']}`)};
`;

export const Footer = styled.div`
  flex-shrink: 0;
  border-top: ${size.stroke['stroke-default']} solid ${semantic.border.default};
`;
